import { computed, inject, Injectable } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { Order, QueryConfig, QueryEntity } from '@datorama/akita';

import { BudgetType, EntityType, OrganizationType } from '@shared/services/gql.service';

import { OrganizationStore, VendorsState } from './organization.store';

@Injectable({ providedIn: 'root' })
@QueryConfig({
  sortBy: 'name',
  sortByOrder: Order.ASC,
})
export class OrganizationQuery extends QueryEntity<VendorsState> {
  allReceivesEstimateVendors$ = this.selectAll({
    filterBy: (entity) => {
      return (
        entity.organization_type === OrganizationType.ORGANIZATION_VENDOR &&
        !!entity.receives_vendor_estimate
      );
    },
  });

  allReceivesEstimateVendors = toSignal(this.allReceivesEstimateVendors$, { requireSync: true });

  allVendors$ = this.selectAll({
    filterBy: (entity) => entity.organization_type === OrganizationType.ORGANIZATION_VENDOR,
  });

  allVendors = toSignal(this.allVendors$, { requireSync: true });
  loading = toSignal(this.selectLoading(), { requireSync: true });

  allVendorsLength = computed(() => {
    const vendors = this.allVendors();

    return vendors.length;
  });

  allVendorsObj$ = this.selectAll({
    asObject: true,
    filterBy: (entity) => entity.organization_type === OrganizationType.ORGANIZATION_VENDOR,
  });
  protected store: OrganizationStore;

  constructor() {
    const store = inject(OrganizationStore);

    super(store);

    this.store = store;
  }

  getAllReceivesEstimateVendors() {
    return this.getAll({
      filterBy: (entity) =>
        entity.organization_type === OrganizationType.ORGANIZATION_VENDOR &&
        !!entity.receives_vendor_estimate,
    });
  }

  getAllVendors() {
    return this.getAll({
      filterBy: (entity) => entity.organization_type === OrganizationType.ORGANIZATION_VENDOR,
    });
  }

  getBudgetVersion(
    org_id: string,
    budget_type: BudgetType,
    entity_id: string,
    entity_type: EntityType
  ) {
    const bv = this.getEntity(org_id)?.budget_version;

    return bv?.[`${budget_type}-${entity_id}-${entity_type}`];
  }

  getPrimaryBudgetVersion(org_id: string) {
    const bv = this.getEntity(org_id)?.budget_version;

    return bv?.[`${BudgetType.BUDGET_PRIMARY}-${org_id}-${EntityType.ORGANIZATION}`];
  }

  getVendor(vendor_id: string) {
    return this.getAll({
      filterBy: (entity) =>
        entity.organization_type === OrganizationType.ORGANIZATION_VENDOR &&
        entity.id === vendor_id,
    });
  }

  hasReceivesEstimateVendor(vendor_id: string) {
    return this.allReceivesEstimateVendors().find((v) => v.id === vendor_id);
  }

  isVendorHasBudget(vendor_id: string) {
    return (this.getVendor(vendor_id)?.[0]?.current_budget_versions?.length || 0) > 0;
  }
}
